import { flushSync } from 'react-dom';
import withoutTransitions from '../utils/withoutTransitions';
import './themeSwitch.css';

/**
 * Switch light and dark as one even fade instead of in patches.
 *
 * The theme is a class on <body>, and about fifty rules across the app give their own element a
 * background transition, each at its own speed, while everything else changes at once. So a
 * switch used to land in stages. Where the browser has the View Transitions API, the whole page
 * is snapshotted and cross-faded from the old theme to the new one. Elsewhere, every element gets
 * the same short colour transition for the length of the switch only (themeSwitch.css).
 *
 * With View Transitions (and with no animation at all) the colours change with every element's
 * own transition off, so the cross-fade is the only motion (utils/withoutTransitions.js).
 *
 * `apply` must make the change synchronously: it is run inside the transition's callback, and
 * the new state has to be on the page when that callback returns. flushSync makes a React state
 * update (the logo swap, the toggle icon) land in time.
 */
const FALLBACK_MS = 400;
let fallbackTimer = null;

const prefersReducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function runThemeSwitch(apply) {
  const commit = () => withoutTransitions(() => flushSync(apply));
  if (typeof document === 'undefined' || prefersReducedMotion() || document.visibilityState === 'hidden') {
    commit();
    return;
  }
  if (typeof document.startViewTransition === 'function') {
    document.startViewTransition(commit);
    return;
  }
  const root = document.documentElement;
  root.classList.add('theme-switching');
  flushSync(apply);
  window.clearTimeout(fallbackTimer);
  fallbackTimer = window.setTimeout(() => root.classList.remove('theme-switching'), FALLBACK_MS);
}
